import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FiUserPlus, FiUserMinus, FiUsers, FiUserCheck } from 'react-icons/fi';
import ProfileHeader from './ProfileHeader';
import AboutTab from './tabs/AboutTab';
import SkillsTab from './tabs/SkillsTab';
import EmploymentTab from './tabs/EmploymentTab';
import LearningsTab from './tabs/LearningsTab';
import OthersTab from './tabs/OthersTab';
import PublicPost from './PublicPost';
import VerificationOverlay from './shared/VerificationOverlay';
import { useUserDetailData } from '../../hooks/useUserDetailData';

const UserProfile = ({ 
  userData, 
  userId, 
  privacySettings, 
  accessDenied: propAccessDenied, 
  accessMessage: propAccessMessage, 
  profileUserId: propProfileUserId,
  // Props for Posts tab layout
  handleFollow,
  handleUnfollow,
  followLoading,
  unfollowLoading,
  isFollowing: propIsFollowing,
  isModerator: propIsModerator,
  isAuthenticated: propIsAuthenticated,
  onRequireAuth,
  userProfile: propUserProfile,
  sentFollowRequests = []
}) => {

  // Get MYST ID and user data from props or localStorage
  const getAuthData = () => {
    // ALWAYS prioritize userId prop if provided (this is the profile being viewed)
    // This ensures we fetch data for the correct user, not the current logged-in user
    if (userId) {
      // Use provided userData if available, otherwise will be populated from API
      return { 
        userId, 
        userData: userData || {
          name: "",
          avatar: "",
          email: "",
          phone: "",
          location: "",
          mobileNumber: "",
          city: "",
          country: "",
          bio: "",
          summary: "",
          dob: "",
          gender: "",
          address1: ""
        }
      };
    }

    // Fallback to localStorage only if userId prop is not provided (viewing own profile)
    try {
      const profile = JSON.parse(localStorage.getItem('profile'));
      const user = profile?.user;

      if (!user) {
        return { userId: null, userData: null, userObjectId: null };
      }

      const mystId = user.mystId || '';
      const formattedUserData = {
        name: user.name || user.fullName || '',
        email: user.email || '',
        avatar: user.avatar || user.profilePicture || '',
        profilePictureFileName: user.profilePictureFileName || '',
        profilePictureFileId: user.profilePictureFileId || '',
        phone: user.phone || user.phoneNumber || '',
        location: user.location || user.address || '',
        mobileNumber: user.mobileNumber || '',
        city: user.city || '',
        country: user.country || '',
        dob: (() => {
          if (!user.dob) return '';
          const dobNumber = Number(user.dob);
          if (isNaN(dobNumber)) return user.dob; 
          const date = new Date(dobNumber);
          return date.toLocaleDateString('en-GB');
        })(), gender: user.gender || '',
        address1: user.address1 || '',
        bio: user.bio || '',
        summary: user.summary || ''
      };

      return { userId: mystId, userData: formattedUserData, userObjectId: user._id };
    } catch (error) {
      console.error('Error getting auth data:', error);
      return { userId: null, userData: null, userObjectId: null };
    }
  };

  // Get the actual MYST ID and user data
  // actualUserId will be the profile owner's ID (from userId prop), not the current user's ID
  const { userId: actualUserId, userData: actualUserData, userObjectId } = getAuthData();
  
  // Get current logged-in user data from Redux
  const currentUserData = useSelector((state) => state.auth?.userData);
  
  // Check if viewing own profile
  const isViewingOwnProfile = useMemo(() => {
    if (!currentUserData?._id || !propProfileUserId) return false;
    return String(currentUserData._id) === String(propProfileUserId);
  }, [currentUserData?._id, propProfileUserId]);

  // Check if there's a pending follow request for this profile
  const existingFollowRequest = useMemo(() => {
    if (!propProfileUserId || !sentFollowRequests || sentFollowRequests.length === 0) {
      return null;
    }
    
    const found = sentFollowRequests.find((request) => {
      // Handle both cases: recipient as object with _id or as string ID
      const recipientId = request.recipient?._id || request.recipient;
      const matches = recipientId && String(recipientId) === String(propProfileUserId);
      return matches && request.status === "pending";
    });
    
    return found;
  }, [sentFollowRequests, propProfileUserId]);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState("Posts");

  // State for About tab
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [isPersonalInfoExpanded, setIsPersonalInfoExpanded] = useState(true);
  const [isWorkLocationsExpanded, setIsWorkLocationsExpanded] = useState(true);
  
  // State for Skills tab
  const [selectedSkill, setSelectedSkill] = useState("");
  const [isTopSkillsExpanded, setIsTopSkillsExpanded] = useState(true);
  const [isSkillsExpanded, setIsSkillsExpanded] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [isMainProjectExpanded, setIsMainProjectExpanded] = useState(false);

  // State for Employment tab
  const [isNHSExpanded, setIsNHSExpanded] = useState(true);
  const [isOpenAIExpanded, setIsOpenAIExpanded] = useState(false);
  const [isUnitedHealthExpanded, setIsUnitedHealthExpanded] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [isExperienceCardExpanded, setIsExperienceCardExpanded] = useState(false);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);

  // State for Learnings tab
  const [isEducationExpanded, setIsEducationExpanded] = useState(true);
  const [isCertificationsExpanded, setIsCertificationsExpanded] = useState(false);
  const [isTrainingsExpanded, setIsTrainingsExpanded] = useState(false);
  const [selectedLearning, setSelectedLearning] = useState(null);
  const [isLearningCardExpanded, setIsLearningCardExpanded] = useState(false);
  const [isLearningDescriptionVisible, setIsLearningDescriptionVisible] = useState(false);

  // State for Others tab
  const [isAwardsExpanded, setIsAwardsExpanded] = useState(true);
  const [isLicensesExpanded, setIsLicensesExpanded] = useState(false);
  const [isAchievementsExpanded, setIsAchievementsExpanded] = useState(false);
  const [isMembershipsExpanded, setIsMembershipsExpanded] = useState(false);
  const [isLanguagesExpanded, setIsLanguagesExpanded] = useState(false);
  const [isOtherExpanded, setIsOtherExpanded] = useState(false);
  const [selectedOther, setSelectedOther] = useState(null);
  const [isOtherCardExpanded, setIsOtherCardExpanded] = useState(false);
  const [isOtherDescriptionVisible, setIsOtherDescriptionVisible] = useState(false);

  // State for verification overlay
  const [verificationOverlay, setVerificationOverlay] = useState({
    show: false,
    validationData: null
  });

  // Use unified user detail data hook - fetches once and provides data to all tabs
  const {
    skillData,
    employmentData,
    learningsData,
    othersData,
    loading,
    error,
    refetch,
    accessDenied
    // accessReason and accessMessage are returned by the hook but not used in this component
    // They are used in ProfileInfoCard via props from PublicProfile
  } = useUserDetailData(actualUserId);

  // Use user data from localStorage (includes mobileNumber, city, country from backend)
  const user = actualUserData || {
    name: "",
    avatar: "",
    email: "",
    phone: "",
    location: "",
    mobileNumber: "",
    city: "",
    country: "",
    bio: "",
    summary: "",
    dob: "",
    gender: "",
    address1: ""
  };

  const isPrivateProfile =
    privacySettings?.profileVisibility === "private";
  
  // Use accessDenied from props if provided, otherwise from hook
  const effectiveAccessDenied = propAccessDenied !== undefined ? propAccessDenied : accessDenied;
  
  // Only show locks/mask data if profile is private AND access is denied
  // If access is approved (accessDenied = false), show full profile
  const shouldMaskData = isPrivateProfile && effectiveAccessDenied;

  // Update selected skill when skillData changes
  useEffect(() => {
    if (skillData?.top5Skills?.length > 0) {
      setSelectedSkill(skillData.top5Skills[0]);
    }
  }, [skillData]);

  // Update selected learning when learningsData changes
  useEffect(() => {
    if (learningsData?.education?.length > 0) {
      setSelectedLearning(learningsData.education[0].id);
    } else if (learningsData?.certifications?.length > 0) {
      setSelectedLearning(learningsData.certifications[0].id);
    } else if (learningsData?.trainings?.length > 0) {
      setSelectedLearning(learningsData.trainings[0].id);
    }
  }, [learningsData]);

  // Update selected experience when employmentData changes
  useEffect(() => {
    if (employmentData?.employments?.length > 0) {
      const firstEmployment = employmentData.employments[0];
      if (firstEmployment?.projects?.length > 0) {
        setSelectedExperience(firstEmployment.projects[0].id);
      }
    }
  }, [employmentData]);

  // Update selected other when othersData changes
  useEffect(() => {
    if (othersData?.awards?.length > 0) {
      setSelectedOther(othersData.awards[0].id);
    } else if (othersData?.licenses?.length > 0) {
      setSelectedOther(othersData.licenses[0].id);
    } else if (othersData?.achievements?.length > 0) {
      setSelectedOther(othersData.achievements[0].id);
    } else if (othersData?.memberships?.length > 0) {
      setSelectedOther(othersData.memberships[0].id);
    } else if (othersData?.languages?.length > 0) {
      setSelectedOther(othersData.languages[0].id);
    } else if (othersData?.other?.length > 0) {
      setSelectedOther(othersData.other[0].id);
    }
  }, [othersData]);

  // Note: We no longer return early when accessDenied
  // Instead, we show the profile structure with lock icons and limited info
  // The shouldMaskData prop will ensure locks are shown in all sections

  // Render tab content based on active tab
  const renderTabContent = () => {
    // Get profileUserId: use prop if provided, otherwise try actualUserData._id, otherwise try userObjectId from localStorage
    const profileUserIdForPosts = propProfileUserId || actualUserData?._id || userObjectId;
    
    switch (activeTab) {
      case "Posts":
        if (!profileUserIdForPosts) {
          return (
            <div className="bg-white border rounded-lg p-8 text-center">
              <p className="text-gray-500 font-roboto-flex">Unable to load posts. User ID not available.</p>
            </div>
          );
        }
        return (
          <div className="flex flex-col lg:flex-row gap-2 md:gap-3">
            {/* Left Sidebar - Following/Followers Info + Follow Button */}
            <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-2 lg:order-1">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                {/* Simple Vertical Layout */}
                <div className="space-y-3">
                  {/* Following */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <FiUsers className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700 font-roboto-flex">Following</span>
                    </div>
                    <span className="text-sm font-roboto-flex text-gray-700">
                      {propUserProfile?.totalFollowing !== undefined && propUserProfile?.totalFollowing !== null
                        ? propUserProfile.totalFollowing
                        : (Array.isArray(propUserProfile?.following) ? propUserProfile.following.length : 0)}
                    </span>
                  </div>
                  
                  {/* Followers */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <FiUsers className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700 font-roboto-flex">Followers</span>
                    </div>
                    <span className="text-sm font-roboto-flex text-gray-700">
                      {propUserProfile?.totalFollowers !== undefined && propUserProfile?.totalFollowers !== null
                        ? propUserProfile.totalFollowers
                        : (Array.isArray(propUserProfile?.followers) ? propUserProfile.followers.length : 0)}
                    </span>
                  </div>
                  
                  {/* Divider */}
                  <div className="border-t border-gray-200 my-2"></div>
                  
                  {/* Posts Count */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-700 font-roboto-flex">Posts</span>
                    <span className="text-sm font-roboto-flex text-gray-700">
                      {propUserProfile?.totalPosts ?? 0}
                    </span>
                  </div>
                  
                  {/* Joined Date */}
                  {propUserProfile?.joinedOn && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-700 font-roboto-flex">Joined</span>
                      <span className="text-sm font-roboto-flex text-gray-700 text-xs">
                        {propUserProfile.joinedOn}
                      </span>
                    </div>
                  )}
                  
                  {/* Following Since (if current user follows this profile) */}
                  {propIsFollowing && (
                    <>
                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2">
                          <FiUserCheck className="w-4 h-4 text-gray-600" />
                          <span className="text-sm text-gray-700 font-roboto-flex">Following since</span>
                        </div>
                        <span className="text-sm font-roboto-flex text-gray-700 text-xs">
                          {propUserProfile?.followingSince || 'Recently'}
                        </span>
                      </div>
                    </>
                  )}
                  
                  {/* Mutual Follow Indicator */}
                  {propUserProfile?.profileOwnerFollowsCurrentUser && propIsFollowing && (
                    <div className="flex items-center gap-2 text-xs text-primary bg-primary/10 px-2 py-1.5 rounded-md">
                      <FiUserCheck className="w-3 h-3" />
                      <span className="font-roboto-flex">You follow each other</span>
                    </div>
                  )}
                </div>
                
                {/* Follow/Unfollow Button */}
                {handleFollow && handleUnfollow && !isViewingOwnProfile && propProfileUserId && (
                  <div className="w-full pt-3 border-t border-gray-200">
                    {propIsFollowing ? (
                      <button
                        onClick={() => {
                          if (propProfileUserId && !unfollowLoading) {
                            handleUnfollow(propProfileUserId);
                          }
                        }}
                        disabled={unfollowLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-action border-action bg-action/5 hover:bg-action/10 hover:border-action/80 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {unfollowLoading ? (
                          <span className="text-xs font-flex-regular">Wait</span>
                        ) : (
                          <>
                            <FiUserMinus className="w-4 h-4" />
                            <span className="font-flex-regular">Unfollow</span>
                          </>
                        )}
                      </button>
                    ) : existingFollowRequest ? (
                      <button
                        disabled
                        type="button"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-gray-500 border-gray-300 bg-gray-50 cursor-not-allowed"
                      >
                        <span className="font-flex-regular">Request Pending</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (propProfileUserId && !followLoading) {
                            handleFollow(propProfileUserId);
                          }
                        }}
                        disabled={followLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-primary border-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/80 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {followLoading ? (
                          <span className="text-xs font-flex-regular">Wait</span>
                        ) : (
                          <>
                            <FiUserPlus className="w-4 h-4" />
                            <span className="font-flex-regular">Follow</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Side - Posts */}
            <div className="flex-1 w-full lg:max-w-6xl order-1 lg:order-2 min-w-0">
              <PublicPost 
                publicUserId={profileUserIdForPosts} 
                userName={actualUserData?.name || user?.name || ""} 
              />
            </div>
          </div>
        );

      case "About":
        return (
          <AboutTab
            user={user}
            isAboutExpanded={isAboutExpanded}
            setIsAboutExpanded={setIsAboutExpanded}
            isPersonalInfoExpanded={isPersonalInfoExpanded}
            setIsPersonalInfoExpanded={setIsPersonalInfoExpanded}
            isWorkLocationsExpanded={isWorkLocationsExpanded}
            setIsWorkLocationsExpanded={setIsWorkLocationsExpanded}
            isPrivateProfile={shouldMaskData}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated}
            onRequireAuth={onRequireAuth}
            isViewingOwnProfile={isViewingOwnProfile}
          />
        );

      case "Skills":
        return (
          <SkillsTab
            selectedSkill={selectedSkill}
            setSelectedSkill={setSelectedSkill}
            isTopSkillsExpanded={isTopSkillsExpanded}
            setIsTopSkillsExpanded={setIsTopSkillsExpanded}
            isSkillsExpanded={isSkillsExpanded}
            setIsSkillsExpanded={setIsSkillsExpanded}
            expandedProjects={expandedProjects}
            setExpandedProjects={setExpandedProjects}
            isMainProjectExpanded={isMainProjectExpanded}
            setIsMainProjectExpanded={setIsMainProjectExpanded}
            setVerificationOverlay={setVerificationOverlay}
            skillData={skillData} // Pass skill data from API
            isPrivateProfile={shouldMaskData}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated}
            onRequireAuth={onRequireAuth}
            isViewingOwnProfile={isViewingOwnProfile}
          />
        );

      case "Employment":
        return (
          <EmploymentTab
            isNHSExpanded={isNHSExpanded}
            setIsNHSExpanded={setIsNHSExpanded}
            isOpenAIExpanded={isOpenAIExpanded}
            setIsOpenAIExpanded={setIsOpenAIExpanded}
            isUnitedHealthExpanded={isUnitedHealthExpanded}
            setIsUnitedHealthExpanded={setIsUnitedHealthExpanded}
            selectedExperience={selectedExperience}
            setSelectedExperience={setSelectedExperience}
            isExperienceCardExpanded={isExperienceCardExpanded}
            setIsExperienceCardExpanded={setIsExperienceCardExpanded}
            isDescriptionVisible={isDescriptionVisible}
            setIsDescriptionVisible={setIsDescriptionVisible}
            setVerificationOverlay={setVerificationOverlay}
            employmentData={employmentData} // Pass employment data from API
            isPrivateProfile={shouldMaskData}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated}
            onRequireAuth={onRequireAuth}
            isViewingOwnProfile={isViewingOwnProfile}
          />
        );

      case "Learnings":
        return (
          <LearningsTab
            isEducationExpanded={isEducationExpanded}
            setIsEducationExpanded={setIsEducationExpanded}
            isCertificationsExpanded={isCertificationsExpanded}
            setIsCertificationsExpanded={setIsCertificationsExpanded}
            isTrainingsExpanded={isTrainingsExpanded}
            setIsTrainingsExpanded={setIsTrainingsExpanded}
            selectedLearning={selectedLearning}
            setSelectedLearning={setSelectedLearning}
            isLearningCardExpanded={isLearningCardExpanded}
            setIsLearningCardExpanded={setIsLearningCardExpanded}
            isDescriptionVisible={isLearningDescriptionVisible}
            setIsDescriptionVisible={setIsLearningDescriptionVisible}
            setVerificationOverlay={setVerificationOverlay}
            learningsData={learningsData} // Pass learnings data from API
            isPrivateProfile={shouldMaskData}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated}
            onRequireAuth={onRequireAuth}
            isViewingOwnProfile={isViewingOwnProfile}
          />
        );

      case "Others":
        return (
          <OthersTab
            isAwardsExpanded={isAwardsExpanded}
            setIsAwardsExpanded={setIsAwardsExpanded}
            isLicensesExpanded={isLicensesExpanded}
            setIsLicensesExpanded={setIsLicensesExpanded}
            isAchievementsExpanded={isAchievementsExpanded}
            setIsAchievementsExpanded={setIsAchievementsExpanded}
            isMembershipsExpanded={isMembershipsExpanded}
            setIsMembershipsExpanded={setIsMembershipsExpanded}
            isLanguagesExpanded={isLanguagesExpanded}
            setIsLanguagesExpanded={setIsLanguagesExpanded}
            isOtherExpanded={isOtherExpanded}
            setIsOtherExpanded={setIsOtherExpanded}
            selectedOther={selectedOther}
            setSelectedOther={setSelectedOther}
            isOtherCardExpanded={isOtherCardExpanded}
            setIsOtherCardExpanded={setIsOtherCardExpanded}
            isDescriptionVisible={isOtherDescriptionVisible}
            setIsDescriptionVisible={setIsOtherDescriptionVisible}
            setVerificationOverlay={setVerificationOverlay}
            othersData={othersData} // Pass others data from API
            isPrivateProfile={shouldMaskData}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated}
            onRequireAuth={onRequireAuth}
            isViewingOwnProfile={isViewingOwnProfile}
          />
        );

      default:
        return null;
    }
  };

  // Show loading state for any tab that needs data
  if (loading && (activeTab === "Skills" || activeTab === "Employment" || activeTab === "Learnings" || activeTab === "Others")) {
    return (
      <div className="shadow-sm">
        <div className="p-2 md:p-0">
          {/* Profile Header - Always show */}
          <ProfileHeader
            user={user}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isPrivateProfile={shouldMaskData}
            accessDenied={effectiveAccessDenied}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={true}
            isViewingOwnProfile={isViewingOwnProfile}
          />

          {/* Loading state for data tabs */}
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600 font-roboto-flex">Loading profile data...</p>
            </div>
          </div>

          {/* Verification Overlay */}
          <VerificationOverlay
            verificationOverlay={verificationOverlay}
            setVerificationOverlay={setVerificationOverlay}
          />
        </div>
      </div>
    );
  }

  // Show error state for any tab that needs data
  if (error && (activeTab === "Posts" || activeTab === "Skills" || activeTab === "Employment" || activeTab === "Learnings" || activeTab === "Others")) {
    return (
      <div className="shadow-sm">
        <div className="p-2 md:p-0">
          {/* Profile Header - Always show */}
          <ProfileHeader
            user={user}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isPrivateProfile={shouldMaskData}
            accessDenied={effectiveAccessDenied}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={true}
            isViewingOwnProfile={isViewingOwnProfile}
          />

          {/* Error state for data tabs */}
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-red-600 mb-2 font-roboto-flex">Failed to load profile data</p>
              <button
                onClick={refetch}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-roboto-flex"
              >
                Retry
              </button>
            </div>
          </div>

          {/* Verification Overlay */}
          <VerificationOverlay
            verificationOverlay={verificationOverlay}
            setVerificationOverlay={setVerificationOverlay}
          />
        </div>
      </div>
    );
  }





  // Handle case when no userId is provided
  if (!actualUserId) {
    return (
      <div className="shadow-sm">
        <div className="p-2 md:p-0">
          <ProfileHeader
            user={userData || user}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isPrivateProfile={shouldMaskData}
            accessDenied={effectiveAccessDenied}
            profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
            isAuthenticated={propIsAuthenticated !== undefined ? propIsAuthenticated : true}
            isViewingOwnProfile={isViewingOwnProfile}
          />
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-yellow-500 text-6xl mb-4">⚠️</div>
              <p className="text-yellow-600 mb-2">No User ID Provided</p>
              <p className="text-gray-600 text-sm">Please provide a valid MYST user ID to load profile data.</p>
            </div>
          </div>
          <VerificationOverlay
            verificationOverlay={verificationOverlay}
            setVerificationOverlay={setVerificationOverlay}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="shadow-sm">
      <div className="p-2 md:p-0">
        {/* Profile Header */}
        <ProfileHeader
          user={user}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
            isPrivateProfile={shouldMaskData}
          accessDenied={effectiveAccessDenied}
          profileUserId={propProfileUserId || actualUserData?._id || userObjectId}
          isAuthenticated={propIsAuthenticated !== undefined ? propIsAuthenticated : true}
          isViewingOwnProfile={isViewingOwnProfile}
        />

        {/* Tab Content */}
        {renderTabContent()}

        {/* Verification Overlay */}
        <VerificationOverlay
          verificationOverlay={verificationOverlay}
          setVerificationOverlay={setVerificationOverlay}
        />
      </div>
    </div>
  );
};

export default UserProfile;
